import { CoreConfiguration } from '../../api';
import { BaseAPI, HTTPBody, HTTPHeaders, HTTPMethod, HTTPQuery } from '../../api/server/runtime';

/**
 * A request to a route the generated client has no method for — the provider app's Roster writes
 * and tags, and the proposed re-apply-to-claims routes (see `docs/roster-backend-asks.md`).
 *
 * It goes through the generated runtime's own `request`, with the app's configuration, so it
 * carries the session and passes the same middleware as every generated call: token refresh, the
 * demo-mode guard (NS-14), the abort groups. Nothing in the generated client is edited (NS-16).
 *
 * A route the server does not have fails like any other request, and the error names it —
 * "POST /provider/provider answered 404: Invalid HTTP method used for this path" — so a missing
 * endpoint is visible where it is used rather than guessed at.
 */
class RosterRawApi extends BaseAPI {
    public async send(method: HTTPMethod, path: string, options: { query?: HTTPQuery; body?: unknown } = {}): Promise<unknown> {
        const headers: HTTPHeaders = {};
        if (options.body !== undefined) {
            headers['Content-Type'] = 'application/json';
        }
        const token = this.configuration.accessToken;
        if (token) {
            // The app's token function ignores the scheme and scopes; the generated calls pass them too.
            headers.Authorization = token('standard_auth');
        }
        try {
            const response = await this.request({ path, method, headers, query: options.query, body: options.body as HTTPBody });
            const text = await response.text();
            return text ? JSON.parse(text) : undefined;
        } catch (error) {
            throw await rosterApiError(error, method);
        }
    }
}

/**
 * The generated runtime throws the failed `Response` itself, not an Error — so "request failed" was
 * all a failure could say. This turns it into an Error naming the route, the status and the
 * server's reason: "PUT /provider/provider/… answered 404: Invalid HTTP method used for this path".
 */
export async function rosterApiError(error: unknown, method?: string): Promise<Error> {
    if (typeof Response !== 'undefined' && error instanceof Response) {
        let path = error.url;
        try {
            path = new URL(error.url).pathname;
        } catch {
            // keep the raw url
        }
        return new Error(`${method ? `${method} ` : ''}${path} answered ${error.status}${await reasonOf(error)}`);
    }
    return error instanceof Error ? error : new Error(String(error));
}

/** Runs a client call; a failed response becomes an Error that says which route failed and how. */
export async function rosterCall<T>(call: () => Promise<T>, method?: string): Promise<T> {
    try {
        return await call();
    } catch (error) {
        throw await rosterApiError(error, method);
    }
}

async function reasonOf(response: Response): Promise<string> {
    try {
        const text = await response.clone().text();
        try {
            const json = JSON.parse(text);
            const reason = json?.error ?? json?.message;
            return reason ? `: ${String(reason).slice(0, 160)}` : '';
        } catch {
            return text ? `: ${text.slice(0, 160)}` : '';
        }
    } catch {
        return '';
    }
}

export function rosterRawRequest(
    method: HTTPMethod,
    path: string,
    options: { query?: HTTPQuery; body?: unknown } = {},
): Promise<unknown> {
    return new RosterRawApi(CoreConfiguration).send(method, path, options);
}
